import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { classes } from 'typestyle';
import { SessionUserSelectors } from '../../../../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useFormikContext } from '../../../../FormikWrapperForStubbing';
import { formError } from '../../../../styles/CommonStyles';
import { generatedUrlDivStyle, tableLayout } from '../../../styles/SiteNameAndIdStyles';
import { useSelector } from '../../../utils';
import { SiteSettingsForServerAdminProps } from '../../site-settings/ReactSiteSettingsForServerAdmin';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { calculateUrlFromUrlName, SiteIdValidator, useShouldCopySiteNameToUrl } from './SiteNameAndIdBusinessLogic';
import { SiteNameRow, useSiteNameChangeHandler } from './SiteNameRow';
import { SiteUrlRow, useUrlNameChangeHandler } from './SiteUrlRow';

// individual sections can specify the settings they depend on to clean up their props
export type SiteNameAndIdSectionProps = SiteSettingsForServerAdminProps<'name' | 'urlName'>;

export function SiteNameAndIdSection(props: SiteNameAndIdSectionProps) {
  const formik = useFormikContext<SiteNameAndIdSectionProps>();

  const url = useMemo(
    calculateUrlFromUrlName(formik.values.settings.urlName),
    [formik.values.settings.urlName],
  );

  const shouldCopySiteNameToUrl = useShouldCopySiteNameToUrl(props);
  const [isUrlNameModified, setUrlNameModified] = useState(false);

  const handleSiteNameChange = useSiteNameChangeHandler(formik, shouldCopySiteNameToUrl, isUrlNameModified);
  const handleUrlNameChange = useUrlNameChangeHandler(formik, setUrlNameModified);
  const urlNameValidator = useUrlNameValidator(props.isDefaultSite);

  const isMultisite = !formik.values.hideNameAndId ?? useSelector(SessionUserSelectors.isSessionMultiSite);

  if (!isMultisite) {
    return null;
  }

  return <SettingsSection title={Messages.settings_site_name()} testId={'name-and-id-fields'}>
    <table className={tableLayout}>
      <tbody>
      <SiteNameRow name={formik.values.settings.name}
                   onChange={handleSiteNameChange}/>
      {
        !props.isDefaultSite &&
        <SiteUrlRow urlName={formik.values.settings.urlName}
                    onChange={handleUrlNameChange}
                    validator={urlNameValidator}/>
      }
      <tr>
        <td/>
        <td>
          <div className={classes(generatedUrlDivStyle, 'tb-site-url-preview')}>
            <span>{Messages.url_colon()}</span>
            <span title={url} data-tb-test-id={'origin-url'}>&nbsp; {url}</span>
          </div>

          {
            props.isDefaultSite &&
            <div>
              <span data-tb-test-id={'default-site-url-message'}>
                {Messages.defaultSiteURLDetails()}
              </span>
            </div>
          }

          {
            !props.isDefaultSite &&
            formik.touched?.settings?.urlName &&
            formik.errors?.settings?.urlName &&

            <div className={formError}>
              <span data-tb-test-id={'site-id-error'}>{Messages.siteIDInvalid()}</span>
            </div>
          }
        </td>
      </tr>
      </tbody>
    </table>
  </SettingsSection>;
}

export const SiteNameAndIdSpecs = componentRegistryEntry({
  name: 'SiteNameAndIdSection',
  component: SiteNameAndIdSection,
  meta: {
    urlName: {
      initError: props => SiteIdValidator(props.settings?.urlName),
      initTouched: props => !!SiteIdValidator(props.settings?.urlName)
    }
  }
});

function useUrlNameValidator(isDefaultSite: boolean) {
  return useCallback((value: string) => isDefaultSite ? undefined : SiteIdValidator(value), [isDefaultSite]);
}
